import dayjs from "dayjs";
import { SetStateAction, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useRefetch } from "@/contexts";
import { useSession } from "@/hooks/@custom/useSession";
import api from "@/services/api";

type InitialStatusData<T> = {
  data: {
    original: T[];
    filtered: T[];
  };
  orders: { data: Order[] };
  isLoading: boolean;
  isError: boolean;
};

interface Order {
  id: number;
  code: string;
}

type useVehiclesStatusProps = {
  type: "missing" | "attended" | "dropout";
};
export function useVehiclesStatus<T extends { attended_at: string } | { closedAt: string }>({ type }: useVehiclesStatusProps) {
  const { expedition } = useSession();
  const companyId = expedition?.company.id;

  const { registerRefetch, refetch } = useRefetch();
  const [search, setSearch] = useState("");
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [statusData, setStatusData] = useState<InitialStatusData<T>>({
    data: { original: [], filtered: [] },
    orders: {
      data: []
    },
    isLoading: false,
    isError: false
  });

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const sortByAttendedAt = (queueA: T, queueB: T) => {
      const getDate = (item: T) => {
        if ("attended_at" in item && item.attended_at) return dayjs(item.attended_at).valueOf();
        if ("closedAt" in item && item.closedAt) return dayjs(item.closedAt).valueOf();
        return 0;
      };

      return getDate(queueB) - getDate(queueA);
    };

    const fetchByLicensePlate = async (endpoint: string) => {
      const formattedLicensePlate = search.toUpperCase();
      const { data } = await api.get(`${endpoint}?licensePlate=${formattedLicensePlate}`, { signal });
      if (!signal.aborted) {
        setStatusData((prev) => ({ ...prev, data: { ...prev.data, filtered: data } }));
      }
    };

    const fetchAndSortData = async (endpoint: string) => {
      const { data } = await api.get(endpoint, { signal });
      if (!signal.aborted) {
        const sortedData = data.sort(sortByAttendedAt);
        setStatusData((prev) => ({ ...prev, data: { ...prev.data, original: sortedData } }));
      }
    };

    const fetchAttended = async () => {
      const endpoint = `/queue/attendeds/${companyId}`;

      if (search.length > 0) {
        await fetchByLicensePlate(endpoint);
        return;
      }

      await fetchAndSortData(endpoint);
    };

    const fetchMissing = async () => {
      const endpoint = `/queue/missings/${companyId}`;

      if (search.length > 0) {
        await fetchByLicensePlate(endpoint);
        return;
      }

      const getOrdersCompany = async () => {
        return await api.get<Order[]>("/order", {
          params: { company: companyId },
          signal
        });
      };

      const promises = [api.get(endpoint, { signal })];

      if (!ordersLoaded) {
        promises.push(getOrdersCompany());
      }

      const responses = await Promise.allSettled(promises);

      if (!signal.aborted) {
        if (responses[0].status === "fulfilled") {
          const sortedData = responses[0].value.data.sort(sortByAttendedAt);
          setStatusData((prev) => ({ ...prev, data: { ...prev.data, original: sortedData } }));
        }

        if (!ordersLoaded && responses[1]?.status === "fulfilled") {
          const ordersResponse = responses[1] as PromiseFulfilledResult<{ data: { data: Order[] } }>;
          setStatusData((prev) => ({
            ...prev,
            orders: {
              data: ordersResponse.value.data.data
            }
          }));
          setOrdersLoaded(true);
        }
      }
    };

    const fetchDropout = async () => {
      const endpoint = `/logs/queue-drops/${companyId}`;

      if (search.length > 0) {
        await fetchByLicensePlate(endpoint);
        return;
      }

      const { data } = await api.get(endpoint, { signal });
      if (!signal.aborted) {
        console.log({ data });
        setStatusData((prev) => ({ ...prev, data: { ...prev.data, original: data } }));
      }
    };

    const fetchStrategies: Record<useVehiclesStatusProps["type"], () => Promise<void>> = {
      attended: fetchAttended,
      missing: fetchMissing,
      dropout: fetchDropout
    };

    const executeFetch = async () => {
      const fetchFunction = fetchStrategies[type];

      if (!fetchFunction) {
        throw new Error(`Invalid type: ${type}`);
      }

      if (!signal.aborted) {
        setStatusData((prev) => ({ ...prev, isLoading: true }));
      }

      const fetchKey = `vehicles${type.charAt(0).toUpperCase() + type.slice(1)}` as const;

      try {
        //@ts-expect-error São itens dinamicos
        registerRefetch(fetchKey, fetchFunction);
        await fetchFunction();
      } catch (error) {
        if (!signal.aborted) {
          console.error(`Error fetching ${type} vehicles status:`, error);
          if (error instanceof Error && error.name !== "AbortError") {
            toast.error("Ocorreu um erro ao buscar os veículos.");
            setStatusData((prev) => ({ ...prev, isError: true }));
          }
        }
      } finally {
        if (!signal.aborted) {
          setStatusData((prev) => ({ ...prev, isLoading: false }));
        }
      }
    };

    executeFetch();

    // Cleanup function para cancelar requisições quando o componente for desmontado
    return () => {
      abortController.abort();
    };
  }, [type, search, companyId]);

  return {
    data: {
      original: statusData.data.original,
      filtered: statusData.data.filtered
    },
    orders: statusData.orders,
    state: {
      type,
      isLoading: statusData.isLoading,
      isError: statusData.isError
    },
    actions: {
      setOriginal: (newData: T[]) =>
        setStatusData((prev) => ({
          ...prev,
          data: { ...prev.data, original: newData }
        })),
      setFiltered: (newData: T[]) =>
        setStatusData((prev) => ({
          ...prev,
          data: { ...prev.data, filtered: newData }
        })),
      setData: (original: T[], filtered: T[]) =>
        setStatusData((prev) => ({
          ...prev,
          data: { ...prev.data, original, filtered }
        })),
      setStatus: (updater: SetStateAction<InitialStatusData<T>>) => setStatusData(updater),
      refetch: () =>
        //@ts-expect-error isso não é um erro, é uma chamada dinamica
        refetch(`vehicles${type.charAt(0).toUpperCase() + type.slice(1)}`, {
          onRefetchInit: () => {
            setStatusData((prev) => ({ ...prev, isLoading: true }));
          },
          onRefetchFinally: () => {
            setStatusData((prev) => ({ ...prev, isLoading: false }));
          }
        })
    },
    search: {
      value: search,
      onChange: setSearch
    },
    activeArray: (search.length > 0 ? "filtered" : "original") as "original" | "filtered"
  };
}
